/* eslint-disable unicorn/consistent-function-scoping */
import { useState, useCallback, useRef, useEffect } from 'react';
import {
  AddressElement,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Address, StripeAddressElementChangeEvent } from '@stripe/stripe-js';
import * as Sentry from '@sentry/react';
import human from 'humanparser';

import {
  selectAmounts,
  selectMentor,
  selectLicenseCount,
  selectTrackGroup,
  selectSessionId,
  setEmail,
  setPaymentIntentId,
  setSessionId,
  setMentor,
  setUpdateToken,
  setMentorUserId,
  setIsReturningMentor,
} from '../../../features/signUp/signUpSlice';
import { handleError } from '../../../services/ErrorService';
import { store, useAppSelector } from '../../../stores/AppStore';
import {
  useCreatePaymentIntentMutation,
  useConfirmTrialPaymentMethodMutation,
  useGetPriceConfigQuery,
  useCreateSignUpSessionMutation,
  useUpdateSignUpSessionMutation,
  useMentorSignupMutation,
  useLazyCheckEmailExistsQuery,
} from '../../../services/apiSlice';
import { hasMailtoPrefix, normalizeEmail } from '../../../utils/email';
import {
  emailPattern,
  getStripeAddressElementOptions,
  getStripePaymentElementOptions,
  TRIAL_PERIOD_DAYS,
} from '../../../constants';
import OrderDetails from './OrderDetails';
import { Link } from 'react-router-dom';
import { BillingPeriod } from '../../../types';
import {
  logPaymentDebugInfo,
  validatePaymentRequirements,
} from '../../../utils/payment-debug';

/**
 * Detects Apple Pay context for debugging cross-device handoff issues.
 * Mac Safari users without Touch ID require iPhone handoff for Apple Pay.
 * This helper provides context for Sentry logging without exposing PII.
 */
const detectApplePayContext = () => ({
  isApplePayCapable:
    typeof window !== 'undefined' && 'ApplePaySession' in window,
  isMacOS: /Macintosh|Mac OS X/.test(navigator.userAgent),
  isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
  isSafari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
  // Cross-device scenario: Mac Safari without Touch ID needs iPhone handoff
  requiresCrossDeviceHandoff:
    /Macintosh|Mac OS X/.test(navigator.userAgent) &&
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
  browserVersion: navigator.userAgent.match(/Version\/(\d+)/)?.[1] || 'unknown',
  platform: navigator.platform,
});

interface EmbeddedCardFormProperties {
  onNextStep: (paymentType: 'trial' | 'payment' | 'discounted') => void;
  onPreviousStep: () => void;
}

const EmbeddedCardForm = ({
  onNextStep,
  onPreviousStep,
}: EmbeddedCardFormProperties) => {
  const stripe = useStripe();
  const elements = useElements();
  const [{ addressBlocked, cardBlocked }, setBlocked] = useState({
    addressBlocked: true,
    cardBlocked: true,
  });
  const mentor = useAppSelector(selectMentor);
  const sessionId = useAppSelector(selectSessionId);
  const [address, setAddress] = useState<Address>();
  const [name, setName] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const amounts = useAppSelector(selectAmounts);
  const licenseCount = useAppSelector(selectLicenseCount);
  const trackGroup = useAppSelector(selectTrackGroup);
  const [createPaymentIntentMutation, { error: paymentIntentError }] =
    useCreatePaymentIntentMutation();
  const [confirmTrialPaymentMethodMutation] =
    useConfirmTrialPaymentMethodMutation();
  const [createSignUpSession] = useCreateSignUpSessionMutation();
  const [updateSignUpSession] = useUpdateSignUpSessionMutation();
  const [mentorSignup] = useMentorSignupMutation();
  const [checkEmailExists] = useLazyCheckEmailExistsQuery();
  const {
    data: priceConfig,
    isLoading: isPriceConfigLoading,
    error: priceConfigError,
    refetch: refetchPriceConfig,
  } = useGetPriceConfigQuery();

  // Email validation state for non-mentor existing users
  const [emailValidationError, setEmailValidationError] = useState<
    string | undefined
  >(undefined);

  const isEmailValid = emailPattern.test(mentor.email);

  // Keep the sessionId ref in sync with Redux state
  useEffect(() => {
    currentSessionIdReference.current = sessionId;
  }, [sessionId]);

  const getPriceId = (
    billingPeriod: BillingPeriod,
    licenseCount: number,
  ): string => {
    if (!priceConfig) {
      console.error('Price configuration not loaded', {
        isLoading: isPriceConfigLoading,
        hasError: !!priceConfigError,
      });
      return '';
    }

    const priceMap = {
      month: priceConfig.monthly,
      '3month': priceConfig['3monthly'],
      year: priceConfig.yearly,
    };

    const priceIds = priceMap[billingPeriod];
    const priceId = priceIds?.[licenseCount];

    if (!priceId) {
      console.error(
        `No price ID found for ${billingPeriod} billing with ${licenseCount} licenses`,
        {
          billingPeriod,
          licenseCount,
          availablePrices: priceMap[billingPeriod],
          fullPriceConfig: priceConfig,
        },
      );

      // Show user-friendly error
      handleError(
        new Error(
          `Pricing unavailable for ${licenseCount} license${
            licenseCount > 1 ? 's' : ''
          } with ${billingPeriod} billing. Please try refreshing the page or contact support.`,
        ),
        {
          displayedError:
            'Pricing information temporarily unavailable. Please refresh the page.',
        },
      );
      return '';
    }

    return priceId;
  };

  const handleAddressChange = async (
    event: StripeAddressElementChangeEvent,
  ) => {
    // Check if we have enough address information for tax calculation
    // We need city, state, and postal_code - don't wait for Stripe's "complete" flag
    const hasRequiredFields =
      event.value.address &&
      event.value.address.city &&
      event.value.address.state &&
      event.value.address.postal_code;

    if (!hasRequiredFields) {
      setBlocked((previous) => ({ ...previous, addressBlocked: true }));
      return;
    }

    // Check if address has changed (for tax calculation purposes)
    const addressChanged = !(
      address &&
      address.city === event.value.address.city &&
      address.postal_code === event.value.address.postal_code &&
      address.state === event.value.address.state
    );

    // Only save address if it has actually changed
    if (addressChanged) {
      setName(event.value.name);
      setAddress(event.value.address);

      // Extract and parse name from billing address for mentor account
      if (event.value.name) {
        // Use humanparser for better name parsing (handles titles, suffixes, etc.)
        const parsedName = human.parseName(event.value.name);
        const firstName = parsedName.firstName || '';
        const lastName = parsedName.lastName || '';

        // Update Redux with parsed name (will be used for mentor signup)
        // Use fresh state from store to avoid stale closure issues
        const currentMentor = store.getState().signUp.mentor;
        store.dispatch(
          setMentor({
            ...currentMentor,
            firstName,
            lastName,
          }),
        );
      }
    }

    // Always update the blocked state based on Stripe's complete status
    // This ensures the payment button gets unblocked when address becomes complete
    setBlocked((previous) => ({
      ...previous,
      addressBlocked: !event.complete,
    }));
  };

  // Create or update sign-up session when email changes
  const emailTimeoutReference = useRef<NodeJS.Timeout>();
  const isCreatingSessionReference = useRef(false);
  const currentSessionIdReference = useRef<string | null>(null);

  const handleEmailChange = useCallback(
    async (email: string) => {
      // Clear any pending timeout
      if (emailTimeoutReference.current) {
        clearTimeout(emailTimeoutReference.current);
      }

      // Update email in store immediately - strip mailto prefix if user pasted one
      const sanitizedEmail = hasMailtoPrefix(email)
        ? normalizeEmail(email)
        : email;
      if (sanitizedEmail === email) {
        setEmailValidationError(undefined);
      } else {
        setEmailValidationError(
          "It looks like the email was copied with a 'mailto:' prefix. We've removed it, please check that the address is correct.",
        );
      }
      store.dispatch(setEmail(sanitizedEmail));

      // Only create/update session if email is valid
      if (!emailPattern.test(sanitizedEmail)) {
        return;
      }

      // Debounce session creation/update by 1 second
      emailTimeoutReference.current = setTimeout(async () => {
        // Check if a session creation is already in progress
        if (isCreatingSessionReference.current) {
          console.log('Session creation already in progress, skipping...');
          return;
        }

        try {
          // Check if email belongs to an existing non-mentor user
          const emailCheckResult = await checkEmailExists({ email }).unwrap();
          if (emailCheckResult.exists && !emailCheckResult.isMentor) {
            // Email belongs to an existing non-mentor user (thinker, student, teacher)
            setEmailValidationError(
              'This email is associated with an existing account that cannot make purchases. Please contact help@thinktankprep.com for assistance.',
            );
            return; // Don't create/update session for invalid emails
          } else if (emailCheckResult.hasIncompleteSignup) {
            // Mentor with incomplete signup (paid but never created thinkers)
            const licenseText =
              emailCheckResult.unfulfilledLicenses === 1
                ? '1 unused license'
                : `${emailCheckResult.unfulfilledLicenses} unused licenses`;
            setEmailValidationError(
              `Your previous signup was incomplete. You have ${licenseText} from a prior purchase. Please check your inbox for a recovery link or contact help@thinktankprep.com.`,
            );
            return; // Don't create/update session - block duplicate payment
          } else {
            setEmailValidationError(undefined);
          }

          // Collect current form data
          const formData = {
            mentor: {
              email,
              firstName: mentor.firstName,
              lastName: mentor.lastName,
              phone: mentor.phone,
            },
            licenseCount,
            trackGroup,
            amounts,
          };

          // Use refs to get the most current sessionId
          const currentSessionId =
            sessionId || currentSessionIdReference.current;

          if (currentSessionId) {
            // Update existing session
            await updateSignUpSession({
              sessionId: currentSessionId,
              email, // Include email to handle incremental typing
              currentStep: 1,
              formData,
            }).unwrap();

            console.log('Updated sign-up session:', currentSessionId);
          } else {
            // Mark that we're creating a session
            isCreatingSessionReference.current = true;

            // Create new session
            const result = await createSignUpSession({
              email,
              currentStep: 1, // Payment step
              formData,
            }).unwrap();

            // Store the sessionId in both Redux and ref
            store.dispatch(setSessionId(result.sessionId));
            currentSessionIdReference.current = result.sessionId;
            console.log('Created sign-up session:', result.sessionId);
          }
        } catch (error) {
          console.error('Failed to create/update sign-up session:', error);
          // Don't show error to user - session creation is for recovery purposes
        } finally {
          // Always clear the flag when done
          isCreatingSessionReference.current = false;
        }
      }, 1000);
    },
    [
      sessionId,
      mentor,
      licenseCount,
      trackGroup,
      amounts,
      createSignUpSession,
      updateSignUpSession,
      checkEmailExists,
    ],
  );

  type PaymentSetupResult =
    | { type: 'payment_required'; clientSecret: string }
    | {
        type: 'trial_setup_required';
        clientSecret: string;
        setupIntentId: string;
        subscriptionId: string;
      }
    | { type: 'already_paid' };

  const getClientSecret = async (): Promise<PaymentSetupResult | undefined> => {
    setIsLoading(true);

    // Log payment initiation
    console.log('[Apple Pay Debug] Starting payment intent creation', {
      totalAmount: amounts?.total,
      billingPeriod: amounts?.billingPeriod,
      licenseCount,
      hasDiscountCode: !!amounts?.discountCode,
      hasAddress: !!address,
      hasName: !!name,
    });

    try {
      if (!amounts?.total && amounts?.total !== 0) {
        const errorMessage =
          'Could not process payment as total amount was not calculated.';
        handleError(new Error(errorMessage), { displayedError: errorMessage });
        return undefined;
      }
      if (!address || !name) {
        const errorMessage =
          'Could not process payment as name or address is missing.';
        handleError(new Error(errorMessage), { displayedError: errorMessage });
        return undefined;
      }
      const billingPeriod = (amounts.billingPeriod as BillingPeriod) ?? 'month';

      // Log API call parameters
      console.log('[Apple Pay Debug] Calling createPaymentIntent API', {
        amount: amounts.total,
        email: mentor.email,
        billingPeriod,
        priceId: getPriceId(billingPeriod, licenseCount),
        licenseCount,
        trialPeriodDays: TRIAL_PERIOD_DAYS,
        hasDiscountCode: !!amounts.discountCode,
      });

      const result = await createPaymentIntentMutation({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        amount: amounts.total,
        email: mentor.email,
        address,
        name,
        billingPeriod,
        priceId: getPriceId(billingPeriod, licenseCount),
        licenseCount,
        discountCode: amounts.discountCode,
        trialPeriodDays: TRIAL_PERIOD_DAYS,
      }).unwrap();

      // Log API response
      console.log('[Apple Pay Debug] Received payment intent response', {
        hasResult: !!result,
        requiresSetupConfirmation:
          'requiresSetupConfirmation' in result
            ? result.requiresSetupConfirmation
            : false,
        requiresPayment:
          'requiresPayment' in result ? result.requiresPayment : false,
        isTrial: 'isTrial' in result ? result.isTrial : false,
        hasClientSecret:
          'client_secret' in result ? !!result.client_secret : false,
        // Include debug info if available
        debug: 'debug' in result ? (result as any).debug : undefined,
      });

      if (paymentIntentError) {
        handleError(paymentIntentError);
        setIsLoading(false);
        return undefined;
      }
      if (!result) {
        setIsLoading(false);
        return undefined;
      }

      // Handle trial subscription with SetupIntent
      if (
        'requiresSetupConfirmation' in result &&
        result.requiresSetupConfirmation
      ) {
        const trialResult = result as {
          requiresPayment: true;
          requiresSetupConfirmation: true;
          client_secret: string;
          setupIntentId: string;
          subscriptionId: string;
          isTrial: true;
          debug?: any;
        };

        // Enhanced logging of trial setup details (DO NOT log full client_secret)
        console.log('[Apple Pay Debug] Trial subscription setup required', {
          setupIntentId: trialResult.setupIntentId,
          subscriptionId: trialResult.subscriptionId,
          hasClientSecret: !!trialResult.client_secret,
          debugInfo: trialResult.debug,
        });

        // Sentry breadcrumb
        Sentry.addBreadcrumb({
          category: 'payment',
          message: 'Trial setup required - client secret received',
          level: 'info',
          data: {
            setupIntentId: trialResult.setupIntentId,
            subscriptionId: trialResult.subscriptionId,
            paymentMethodTypes: trialResult.debug?.paymentMethodTypes,
          },
        });

        store.dispatch(setPaymentIntentId(trialResult.subscriptionId));
        setIsLoading(false);

        return {
          type: 'trial_setup_required',
          clientSecret: trialResult.client_secret,
          setupIntentId: trialResult.setupIntentId,
          subscriptionId: trialResult.subscriptionId,
        };
      }

      // Handle regular payment (immediate charging)
      if (result.requiresPayment && 'paymentIntentId' in result) {
        const paymentResult = result as {
          requiresPayment: true;
          client_secret: string;
          paymentIntentId: string;
        };
        store.dispatch(setPaymentIntentId(paymentResult.paymentIntentId));
        return {
          type: 'payment_required',
          clientSecret: paymentResult.client_secret,
        };
      }

      // This should not happen with the cleaned up flows
      throw new Error(
        'Unexpected response format from payment intent creation',
      );
    } catch (error) {
      // Enhanced error logging
      const errorObject = error as any;
      console.error('[Apple Pay Debug] Payment intent creation failed', {
        error,
        errorMessage: errorObject?.message,
        errorStack: errorObject?.stack,
        amounts,
      });

      // Sentry error capture
      Sentry.captureException(error, {
        tags: {
          payment_error: true,
          get_client_secret_failed: true,
          frontend_error: true,
        },
        extra: {
          amounts,
          licenseCount,
          hasAddress: !!address,
          hasName: !!name,
          errorMessage: errorObject?.message,
        },
      });

      handleError(error);
      setIsLoading(false);
      return undefined;
    }
  };

  const handlePayment = async () => {
    // Debug logging for mobile issues
    const debugInfo = logPaymentDebugInfo();
    const validation = validatePaymentRequirements();

    if (!validation.isValid) {
      console.error('Payment validation failed:', validation.issues);
      Sentry.captureMessage('Payment Validation Failed', {
        level: 'error',
        tags: { payment_error: true, validation_failed: true },
        extra: {
          issues: validation.issues,
          debugInfo,
          userAgent: navigator.userAgent,
        },
      });
      handleError(new Error('Payment validation failed'), {
        displayedError: `Payment setup issue: ${validation.issues.join(
          ', ',
        )}. Please try refreshing the page.`,
      });
      return;
    }

    if (!stripe || !elements) {
      const errorMessage =
        'Could not process payment as a critical detail is missing.';
      console.error('Payment initialization failed:', {
        stripe: !!stripe,
        elements: !!elements,
      });
      Sentry.captureMessage('Payment Initialization Failed', {
        level: 'error',
        tags: { payment_error: true, initialization_failed: true },
        extra: {
          stripe: !!stripe,
          elements: !!elements,
          debugInfo,
          userAgent: navigator.userAgent,
        },
      });
      handleError(new Error(errorMessage), {
        displayedError:
          errorMessage +
          ' Please try again or contact us at help@thinktankprep.com',
      });
      return;
    }
    setIsLoading(true);

    try {
      // For Apple Pay and other wallet payments, we need to minimize async operations
      // before the payment confirmation to maintain user activation context

      // First, submit the form to validate it synchronously
      const submitResult = await elements.submit();
      if (submitResult?.error) {
        console.error('Elements submit failed:', submitResult.error);
        Sentry.captureMessage('Elements Submit Failed', {
          level: 'error',
          tags: {
            payment_error: true,
            elements_submit_failed: true,
            stripe_error_type: submitResult.error.type,
          },
          extra: {
            errorMessage: submitResult.error.message,
            debugInfo,
            userAgent: navigator.userAgent,
          },
        });
        handleError(submitResult.error, {
          displayedError:
            submitResult.error.message || 'Payment form validation failed',
        });
        setIsLoading(false);
        return;
      }

      // Now get the client secret and handle the payment/setup
      const setupResult = await getClientSecret();
      if (!setupResult) {
        handleError(new Error('Payment setup could not be completed'), {
          displayedError:
            'Failed to process payment. Please try again or contact us at help@thinktankprep.com',
        });
        setIsLoading(false);
        return;
      }

      // Handle trial setup required
      if (setupResult.type === 'trial_setup_required') {
        const applePayContext = detectApplePayContext();

        // Detailed logging before confirmSetup
        console.log('[Apple Pay Debug] Initiating confirmSetup for trial', {
          setupIntentId: setupResult.setupIntentId,
          subscriptionId: setupResult.subscriptionId,
          hasClientSecret: !!setupResult.clientSecret,
          stripeAvailable: !!stripe,
          elementsAvailable: !!elements,
          ...applePayContext,
        });

        // Sentry breadcrumb with Apple Pay context
        Sentry.addBreadcrumb({
          category: 'apple_pay',
          message: 'Starting confirmSetup for trial',
          level: 'info',
          data: {
            setupIntentId: setupResult.setupIntentId,
            subscriptionId: setupResult.subscriptionId,
            ...applePayContext,
          },
        });

        // Track cross-device handoff initiation
        if (applePayContext.requiresCrossDeviceHandoff) {
          Sentry.setTag('apple_pay_cross_device', 'true');
        }

        // Set up a timeout to capture "stuck" Apple Pay flows where user doesn't
        // complete the cross-device handoff (e.g., iPhone prompt never appears).
        // This ensures we capture breadcrumbs even when no error is thrown.
        // 10 seconds - if handoff doesn't appear by then, it's broken.
        const APPLE_PAY_TIMEOUT_MS = 10_000; // 10 seconds
        let timeoutId: NodeJS.Timeout | undefined;

        if (applePayContext.requiresCrossDeviceHandoff) {
          timeoutId = setTimeout(() => {
            Sentry.captureMessage('Apple Pay Cross-Device Handoff Timeout', {
              level: 'warning',
              tags: {
                payment_warning: true,
                apple_pay_timeout: true,
                apple_pay_cross_device: true,
              },
              extra: {
                setupIntentId: setupResult.setupIntentId,
                subscriptionId: setupResult.subscriptionId,
                applePayContext,
                timeoutMs: APPLE_PAY_TIMEOUT_MS,
                message:
                  'User did not complete Apple Pay within timeout. Possible cross-device handoff failure.',
              },
            });
          }, APPLE_PAY_TIMEOUT_MS);
        }

        const confirmSetupResponse = await stripe.confirmSetup({
          elements,
          clientSecret: setupResult.clientSecret,
          confirmParams: {
            return_url: `${process.env.REACT_APP_BASE_URL}/sign-up`,
          },
          redirect: 'if_required',
        });

        // Clear the timeout since confirmSetup completed (success or error)
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        // Log confirmSetup response
        console.log('[Apple Pay Debug] confirmSetup completed', {
          hasError: !!confirmSetupResponse.error,
          hasSetupIntent: !!confirmSetupResponse.setupIntent,
          setupIntentStatus: confirmSetupResponse.setupIntent?.status,
          hasPaymentMethod: !!confirmSetupResponse.setupIntent?.payment_method,
          errorType: confirmSetupResponse.error?.type,
          errorCode: confirmSetupResponse.error?.code,
          errorMessage: confirmSetupResponse.error?.message,
        });

        if (confirmSetupResponse.error) {
          // Enhanced error logging with Apple Pay context
          console.error('[Apple Pay Debug] confirmSetup failed', {
            error: confirmSetupResponse.error,
            errorType: confirmSetupResponse.error.type,
            errorCode: confirmSetupResponse.error.code,
            errorMessage: confirmSetupResponse.error.message,
            setupIntentId: setupResult.setupIntentId,
            subscriptionId: setupResult.subscriptionId,
            ...applePayContext,
          });

          Sentry.captureMessage('Setup Confirmation Failed', {
            level: 'error',
            tags: {
              payment_error: true,
              setup_confirmation_failed: true,
              trial_setup: true,
              stripe_error_type: confirmSetupResponse.error.type,
              stripe_error_code: confirmSetupResponse.error.code || 'unknown',
              apple_pay_cross_device:
                applePayContext.requiresCrossDeviceHandoff,
            },
            extra: {
              errorMessage: confirmSetupResponse.error.message,
              errorCode: confirmSetupResponse.error.code,
              errorDeclineCode: confirmSetupResponse.error.decline_code,
              setupIntentId: setupResult.setupIntentId,
              subscriptionId: setupResult.subscriptionId,
              applePayContext,
              debugInfo,
            },
          });

          let displayedError =
            'Payment method validation failed. Please try again.';
          switch (confirmSetupResponse.error.type) {
            case 'validation_error':
            case 'card_error': {
              displayedError =
                confirmSetupResponse.error.message || displayedError;
              break;
            }
            case 'api_error':
            case 'api_connection_error':
            case 'idempotency_error':
            case 'authentication_error': {
              displayedError =
                'An error occurred while validating your payment method. Please try again.';
              break;
            }
            case 'rate_limit_error': {
              displayedError =
                'Too many requests. Please try again in a moment.';
              break;
            }
            case 'invalid_request_error': {
              displayedError =
                'An error occurred on our end. Please contact support.';
              break;
            }
          }

          handleError(confirmSetupResponse.error, { displayedError });
          setIsLoading(false);
          return;
        }

        // Setup confirmed successfully, now attach payment method to subscription
        const setupIntent = confirmSetupResponse.setupIntent;

        // Comprehensive status check logging
        console.log('[Apple Pay Debug] SetupIntent confirmation result', {
          status: setupIntent.status,
          hasPaymentMethod: !!setupIntent.payment_method,
          paymentMethodType: typeof setupIntent.payment_method,
          isSucceeded: setupIntent.status === 'succeeded',
        });

        if (setupIntent.status === 'succeeded' && setupIntent.payment_method) {
          const paymentMethodId =
            typeof setupIntent.payment_method === 'string'
              ? setupIntent.payment_method
              : setupIntent.payment_method.id;

          // Log before backend confirmation
          console.log(
            '[Apple Pay Debug] Calling confirmTrialPaymentMethod API',
            {
              setupIntentId: setupResult.setupIntentId,
              subscriptionId: setupResult.subscriptionId,
              hasPaymentMethodId: !!paymentMethodId,
            },
          );

          try {
            await confirmTrialPaymentMethodMutation({
              setupIntentId: setupResult.setupIntentId,
              subscriptionId: setupResult.subscriptionId,
              paymentMethodId,
            }).unwrap();

            // Success logging with Apple Pay context
            console.log(
              '[Apple Pay Debug] Trial payment method confirmed successfully',
              {
                subscriptionId: setupResult.subscriptionId,
                ...applePayContext,
              },
            );

            // Sentry success breadcrumb with Apple Pay context
            Sentry.addBreadcrumb({
              category: 'apple_pay',
              message: 'Trial payment method confirmed successfully',
              level: 'info',
              data: {
                subscriptionId: setupResult.subscriptionId,
                setupIntentId: setupResult.setupIntentId,
                ...applePayContext,
              },
            });

            // Create mentor account immediately after trial setup
            const subscriptionId = setupResult.subscriptionId;
            if (subscriptionId) {
              try {
                // Fallback name parsing using humanparser if mentor name not already set
                const parsedName = name
                  ? human.parseName(name)
                  : { firstName: '', lastName: '' };
                const mentorSignupResult = await mentorSignup({
                  paymentIntentId: subscriptionId,
                  mentor: {
                    firstName: mentor.firstName || parsedName.firstName || '',
                    lastName: mentor.lastName || parsedName.lastName || '',
                    email: mentor.email,
                    phone: mentor.phone,
                  },
                }).unwrap();

                // Store updateToken and mentorUserId for profile updates
                if (mentorSignupResult.updateToken) {
                  store.dispatch(
                    setUpdateToken(mentorSignupResult.updateToken),
                  );
                }
                store.dispatch(
                  setMentorUserId(mentorSignupResult.mentorUserId),
                );
                store.dispatch(
                  setIsReturningMentor(
                    mentorSignupResult.alreadyExists ?? false,
                  ),
                );
              } catch (error) {
                console.error('Failed to create mentor account:', error);
                // Don't block progress - webhook will create mentor as backup
                Sentry.captureException(error, {
                  tags: { mentor_signup_failed: true, trial: true },
                  extra: { subscriptionId },
                });
              }
            }

            // Update session with payment status
            if (sessionId) {
              try {
                await updateSignUpSession({
                  sessionId,
                  currentStep: 1,
                  status: 'payment_completed',
                  paymentIntentId: subscriptionId,
                }).unwrap();
              } catch (error) {
                console.error(
                  'Failed to update session after trial setup:',
                  error,
                );
              }
            }

            setIsLoading(false);
            onNextStep('trial');
            return;
          } catch (confirmError) {
            // More detailed error logging
            const errorObject = confirmError as any;
            console.error(
              '[Apple Pay Debug] confirmTrialPaymentMethod failed',
              {
                error: confirmError,
                errorMessage: errorObject?.message,
                errorData: errorObject?.data,
                setupIntentId: setupResult.setupIntentId,
                subscriptionId: setupResult.subscriptionId,
                paymentMethodId,
              },
            );

            Sentry.captureMessage('Trial Payment Method Confirmation Failed', {
              level: 'error',
              tags: {
                payment_error: true,
                trial_confirmation_failed: true,
                backend_error: true,
              },
              extra: {
                setupIntentId: setupResult.setupIntentId,
                subscriptionId: setupResult.subscriptionId,
                paymentMethodId,
                confirmError: errorObject?.message,
                confirmErrorData: errorObject?.data,
                debugInfo,
                userAgent: navigator.userAgent,
              },
            });
            handleError(
              new Error('Failed to confirm payment method for trial'),
              {
                displayedError:
                  'Payment method validation succeeded, but trial activation failed. Please contact support.',
              },
            );
            setIsLoading(false);
            return;
          }
        } else {
          // Enhanced logging for unexpected state
          console.error('[Apple Pay Debug] SetupIntent not in expected state', {
            status: setupIntent.status,
            expectedStatus: 'succeeded',
            hasPaymentMethod: !!setupIntent.payment_method,
            setupIntent: {
              id: setupIntent.id,
              status: setupIntent.status,
              usage: setupIntent.usage,
              payment_method: setupIntent.payment_method,
            },
          });

          // Sentry capture for unexpected state
          Sentry.captureMessage('SetupIntent Not In Expected State', {
            level: 'error',
            tags: {
              payment_error: true,
              unexpected_setup_intent_state: true,
            },
            extra: {
              setupIntentId: setupIntent.id,
              status: setupIntent.status,
              expectedStatus: 'succeeded',
              hasPaymentMethod: !!setupIntent.payment_method,
              setupResult,
            },
          });

          handleError(new Error('Setup intent not succeeded'), {
            displayedError: 'Payment method setup failed. Please try again.',
          });
          setIsLoading(false);
          return;
        }
      }

      // Handle regular payment (immediate charging)
      if (setupResult.type === 'payment_required') {
        console.log('Confirming payment with secret');
        const confirmPaymentResponse = await stripe.confirmPayment({
          elements,
          clientSecret: setupResult.clientSecret,
          confirmParams: {
            return_url: `${process.env.REACT_APP_BASE_URL}/sign-up`,
          },
          redirect: 'if_required',
        });

        if (confirmPaymentResponse.error) {
          console.error(
            'Payment confirmation failed:',
            confirmPaymentResponse.error,
          );
          Sentry.captureMessage('Payment Confirmation Failed', {
            level: 'error',
            tags: {
              payment_error: true,
              confirmation_failed: true,
              stripe_error_type: confirmPaymentResponse.error.type,
            },
            extra: {
              errorMessage: confirmPaymentResponse.error.message,
              errorCode: confirmPaymentResponse.error.code,
              debugInfo,
              userAgent: navigator.userAgent,
            },
          });
          let displayedError: string | undefined = '';
          switch (confirmPaymentResponse.error.type) {
            case 'validation_error':
            case 'card_error': {
              displayedError = confirmPaymentResponse.error.message;
              break;
            }
            case 'api_error':
            case 'api_connection_error':
            case 'idempotency_error':
            case 'authentication_error': {
              displayedError =
                'An error occurred while processing your payment. If this issue persists, please contact the support.';
              break;
            }
            case 'rate_limit_error': {
              displayedError =
                'Too many request while trying to process your payment. Please try again later.';
              break;
            }
            case 'invalid_request_error': {
              displayedError =
                'An error occured on our end while processing your payment. Please contact the support.';
              break;
            }
            default: {
              displayedError =
                'An unexpected error occurred. Please try again.';
              break;
            }
          }
          handleError(confirmPaymentResponse.error, {
            displayedError,
          });
          setIsLoading(false);
          return;
        }

        console.log('Payment confirmed successfully');

        // Create mentor account immediately after payment
        const paymentIntentId = confirmPaymentResponse.paymentIntent?.id;
        if (paymentIntentId) {
          try {
            console.log('Creating mentor account...');
            // Fallback name parsing using humanparser if mentor name not already set
            const parsedName = name
              ? human.parseName(name)
              : { firstName: '', lastName: '' };
            const mentorSignupResult = await mentorSignup({
              paymentIntentId,
              mentor: {
                firstName: mentor.firstName || parsedName.firstName || '',
                lastName: mentor.lastName || parsedName.lastName || '',
                email: mentor.email,
                phone: mentor.phone,
              },
            }).unwrap();

            // Store updateToken and mentorUserId for profile updates
            if (mentorSignupResult.updateToken) {
              store.dispatch(setUpdateToken(mentorSignupResult.updateToken));
            }
            store.dispatch(setMentorUserId(mentorSignupResult.mentorUserId));
            store.dispatch(
              setIsReturningMentor(mentorSignupResult.alreadyExists ?? false),
            );

            console.log('Mentor account created successfully', {
              mentorUserId: mentorSignupResult.mentorUserId,
              alreadyExists: mentorSignupResult.alreadyExists,
            });
          } catch (error) {
            console.error('Failed to create mentor account:', error);
            // Don't block progress - webhook will create mentor as backup
            // Show non-blocking warning
            Sentry.captureException(error, {
              tags: { mentor_signup_failed: true },
              extra: { paymentIntentId },
            });
          }
        }

        // Update session with payment status
        if (sessionId) {
          try {
            await updateSignUpSession({
              sessionId,
              currentStep: 1,
              status: 'payment_completed',
              paymentIntentId,
            }).unwrap();
          } catch (error) {
            console.error('Failed to update session after payment:', error);
          }
        }

        setIsLoading(false);
        onNextStep('payment');
        return;
      }

      // Handle already paid invoices
      if (setupResult.type === 'already_paid') {
        console.log('Payment already completed, proceeding to next step');
        setIsLoading(false);
        onNextStep('payment');
        return;
      }
    } catch (error) {
      console.error('Payment process failed with exception:', error);
      Sentry.captureException(
        error instanceof Error ? error : new Error('Unknown payment error'),
        {
          tags: {
            payment_error: true,
            payment_exception: true,
          },
          extra: {
            debugInfo,
            userAgent: navigator.userAgent,
            errorType: typeof error,
          },
        },
      );
      handleError(
        error instanceof Error ? error : new Error('Unknown payment error'),
        {
          displayedError:
            'Payment processing failed. Please try again or contact support.',
        },
      );
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-[-8px] md:mt-0 flex flex-col gap-2 md:gap-4 w-full h-full">
      {/* Pricing Error Message */}
      {priceConfigError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-red-600 font-semibold">
              ⚠️ Pricing Unavailable
            </span>
          </div>
          <p className="text-red-700 text-sm">
            Unable to load current pricing information. Please refresh the page
            or try again later.
          </p>
          <button
            onClick={() => refetchPriceConfig()}
            className="mt-2 text-red-600 text-sm underline hover:no-underline"
          >
            Try Again
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Order Details */}
        <OrderDetails
          address={address}
          isApplyCouponBlocked={isLoading}
          setIsLoading={setIsLoading}
        />

        {/* Payment Elements */}
        {amounts?.amount && amounts?.amount > 0 ? (
          <div className={`flex flex-col gap-2 w-full`}>
            <h1 className="text-2xl md:text-3xl font-medium w-full pb-2 md:pb-4">
              Step 2: Payment Details
            </h1>
            <div data-field="name" className="p-Field">
              <label
                className="p-FieldLabel Label Label--empty font-"
                htmlFor="Field-emailInput"
              >
                Email
              </label>
              <div>
                <input
                  type="email"
                  id="Field-emailInput"
                  name="email"
                  autoComplete="email"
                  placeholder="Email address"
                  aria-invalid={!!emailValidationError}
                  aria-required="true"
                  className={`p-Input-input Input Input--empty border w-full p-3 rounded-l ${
                    emailValidationError
                      ? 'border-red focus:border-red'
                      : 'border-slate-200'
                  }`}
                  value={mentor.email}
                  onChange={(event) => {
                    handleEmailChange(event.target.value.trim().toLowerCase());
                  }}
                />
              </div>
              {emailValidationError && (
                <div className="flex items-start gap-2 p-3 mt-2 bg-red/10 border border-red/30 rounded-md">
                  <svg
                    className="w-5 h-5 text-red flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-red font-medium text-sm">
                    {emailValidationError}
                  </span>
                </div>
              )}
              <div className="AnimateSinglePresence"></div>
            </div>
            <AddressElement
              id="address-element"
              options={getStripeAddressElementOptions({
                // name: mentor.firstName + ' ' + mentor.lastName,
              })}
              onChange={handleAddressChange}
            />
            <PaymentElement
              id="payment-element"
              options={getStripePaymentElementOptions()}
              onChange={(event) => {
                const applePayContext = detectApplePayContext();

                // Log payment method selection with Apple Pay context
                console.log('[Apple Pay Debug] Payment method changed', {
                  complete: event.complete,
                  empty: event.empty,
                  ...applePayContext,
                });

                // Track Apple Pay selection attempts for cross-device scenarios
                if (
                  applePayContext.requiresCrossDeviceHandoff &&
                  event.complete
                ) {
                  Sentry.addBreadcrumb({
                    category: 'apple_pay',
                    message: 'Payment ready - cross-device handoff scenario',
                    level: 'info',
                    data: {
                      requiresCrossDeviceHandoff: true,
                      platform: applePayContext.platform,
                      browserVersion: applePayContext.browserVersion,
                    },
                  });
                }

                // Add Sentry breadcrumb when payment is ready
                if (event.complete) {
                  Sentry.addBreadcrumb({
                    category: 'payment',
                    message: 'Payment element completed',
                    level: 'info',
                    data: {
                      complete: true,
                    },
                  });
                }

                setBlocked((previous) => ({
                  ...previous,
                  cardBlocked: !event.complete,
                }));
              }}
              onReady={() => {
                // Log when Payment Element is ready with Apple Pay context
                const applePayContext = detectApplePayContext();
                console.log('[Apple Pay Debug] Payment Element ready', {
                  ...applePayContext,
                  walletOptions: getStripePaymentElementOptions().wallets,
                });

                Sentry.addBreadcrumb({
                  category: 'apple_pay',
                  message: 'Payment Element initialized',
                  level: 'info',
                  data: {
                    ...applePayContext,
                    walletConfig: getStripePaymentElementOptions().wallets,
                    elementsMode:
                      TRIAL_PERIOD_DAYS > 0 ? 'setup' : 'subscription',
                  },
                });
              }}
              onLoadError={(error) => {
                // Capture Payment Element load errors
                console.error(
                  '[Apple Pay Debug] Payment Element failed to load',
                  error,
                );

                Sentry.captureException(error.error, {
                  tags: {
                    payment_error: true,
                    payment_element_load_error: true,
                    element_type: error.elementType,
                  },
                  extra: {
                    errorMessage: error.error?.message,
                    errorType: error.error?.type,
                    errorCode: error.error?.code,
                    elementType: error.elementType,
                    userAgent: navigator.userAgent,
                  },
                });
              }}
            />
          </div>
        ) : (
          <></>
        )}
      </div>

      {/* Action Buttons */}
      <div
        className={`flex gap-2 pt-4 md:pt-6 w-full justify-center md:justify-between items-center`}
      >
        <button
          data-testid={`back-button`}
          type="button"
          className="
              px-10 py-2 w-max rounded-full border font-medium
              transition-all duration-300 ease-in-out
              hover:shadow hover:outline hover:outline-tttDefault hover:outline-[1px]
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          onClick={onPreviousStep}
        >
          Back
        </button>
        <button
          data-testid={`continue-button`}
          onClick={handlePayment}
          disabled={
            isLoading ||
            isPriceConfigLoading ||
            !priceConfig ||
            licenseCount > 3 ||
            addressBlocked ||
            cardBlocked ||
            !isEmailValid ||
            !trackGroup ||
            !!emailValidationError
          }
          title={
            isPriceConfigLoading
              ? 'Loading pricing information...'
              : priceConfig
              ? licenseCount > 3
                ? 'For 4+ licenses, please contact us to set up your subscriptions'
                : emailValidationError ||
                  (addressBlocked
                    ? 'Please enter your billing address first'
                    : cardBlocked
                    ? 'Please enter your card details'
                    : trackGroup
                    ? isEmailValid
                      ? ''
                      : 'Please enter a valid email address'
                    : 'Please choose a learning path')
              : 'Pricing information unavailable. Please refresh the page.'
          }
          className="
            px-10 py-2 w-max rounded-full
            font-medium text-white bg-tttDefault
            transition-all duration-300 ease-in-out
            hover:shadow hover:bg-opacity-90
            disabled:opacity-50 disabled:cursor-not-allowed
          "
        >
          {isPriceConfigLoading
            ? 'Loading Pricing...'
            : licenseCount > 3
            ? 'Contact Us'
            : 'Start Free Trial'}
        </button>
      </div>

      <p className="text-xs text-gray-600">
        By clicking "Start Free Trial" you acknowledge that you have read and
        agree to the Think Tank's{' '}
        <Link to="/privacy-policy" target="_blank" className="text-tttDefault">
          Privacy Policy
        </Link>{' '}
        and{' '}
        <Link
          to="/terms-and-conditions"
          target="_blank"
          className="text-tttDefault"
        >
          Terms of Service
        </Link>
        . Your {TRIAL_PERIOD_DAYS}-day free trial will begin immediately. You
        can cancel anytime during the trial at no cost. After the trial period,
        you will be automatically charged at the selected billing frequency
        unless cancelled.
      </p>
    </div>
  );
};

export default EmbeddedCardForm;
